import React, { useEffect, useRef, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ReportIcon from '@mui/icons-material/Report';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import GrainIcon from '@mui/icons-material/Grain';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { 
  BrowserMultiFormatReader
} from '@zxing/library';

const CameraDialog = ({ open, onClose }) => {
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const [productData, setProductData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [debugErrorMessage, setDebugErrorMessage] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [newProduct, setNewProduct] = useState({
    barcode: '',
    product_name: '',
    gluten: 'unknown'
  });
  const [cameraFacing, setCameraFacing] = useState('environment'); // 'environment' for back, 'user' for front
  const [zoomLevel, setZoomLevel] = useState(null); // null means use camera default, no zoom applied
  const [availableCameras, setAvailableCameras] = useState([]);
  const [availableZoomLevels, setAvailableZoomLevels] = useState([0.5, 1, 1.5, 2]); // Default zoom options
  const [currentZoomIndex, setCurrentZoomIndex] = useState(1); // Default to 1x (index 1)
  const cache = useRef(new Map());
  const isScanning = useRef(false);

  // Debug mode - can be toggled by setting localStorage item or environment variable
  const isDebugMode = localStorage.getItem('debugMode') === 'true' || 
                     process.env.REACT_APP_DEBUG_MODE === 'true';

  const setUserFriendlyError = (userMessage, debugDetails = '') => {
    setErrorMessage(userMessage);
    setDebugErrorMessage(debugDetails);
  };

  const toggleCamera = () => {
    setCameraFacing(prev => prev === 'environment' ? 'user' : 'environment');
    // Restart scanner with new camera - but do it more smoothly
    if (isScanning.current) {
      // Don't stop completely, just switch camera smoothly
      restartScannerWithNewCamera();
    }
  };

  const toggleZoom = () => {
    // If zoom levels haven't been detected yet, detect them first
    if (availableZoomLevels.length === 0 && videoRef.current && videoRef.current.srcObject) {
      const track = videoRef.current.srcObject.getVideoTracks()[0];
      const detectedZooms = detectAvailableZoomLevels(track);
      if (detectedZooms && detectedZooms.length > 0) {
        // Start with first zoom level (not default)
        setCurrentZoomIndex(0);
        setZoomLevel(detectedZooms[0].value);
        applyZoomToCurrentTrack(detectedZooms[0].value);
        return;
      }
    }
    
    // Cycle through available zoom levels
    const nextIndex = (currentZoomIndex + 1) % availableZoomLevels.length;
    setCurrentZoomIndex(nextIndex);
    
    const nextZoomLevel = availableZoomLevels[nextIndex];
    const zoomValue = typeof nextZoomLevel === 'object' ? nextZoomLevel.value : nextZoomLevel;
    
    setZoomLevel(zoomValue);
    // Apply zoom to current video track
    applyZoomToCurrentTrack(zoomValue);
  };

  const detectAvailableZoomLevels = (track) => {
    if (track && track.getCapabilities && track.getCapabilities().zoom) {
      const capabilities = track.getCapabilities();
      const { min, max, step } = capabilities.zoom;
      
      // Use camera's natural zoom levels, don't map to arbitrary values
      const zoomOptions = [];
      
      // If step is defined, use it to create levels
      if (step && step > 0) {
        for (let zoom = min; zoom <= max; zoom += step) {
          zoomOptions.push({
            value: zoom,
            label: `${zoom.toFixed(1)}×`
          });
        }
      } else {
        // Create 3-5 evenly spaced zoom levels
        const numLevels = Math.min(5, Math.max(3, Math.floor((max - min) / 0.5) + 1));
        for (let i = 0; i < numLevels; i++) {
          const zoom = min + (max - min) * (i / (numLevels - 1));
          zoomOptions.push({
            value: zoom,
            label: `${zoom.toFixed(1)}×`
          });
        }
      }
      
      // Only update available zoom levels for UI, don't set any zoom states
      setAvailableZoomLevels(zoomOptions);
      
      return zoomOptions;
    }
    return null;
  };

  const applyZoomToCurrentTrack = useCallback((targetZoom = null) => {
    if (videoRef.current && videoRef.current.srcObject) {
      const track = videoRef.current.srcObject.getVideoTracks()[0];
      if (track && track.getCapabilities && track.getCapabilities().zoom) {
        const zoomValue = targetZoom || zoomLevel;
        
        // Only apply zoom if a zoom value has been explicitly set
        if (zoomValue !== null && zoomValue !== undefined) {
          track.applyConstraints({
            advanced: [{ zoom: zoomValue }]
          }).catch(e => {
            console.log('Zoom not supported on this device:', e);
          });
        }
      }
    }
  }, [zoomLevel]);

  const restartScannerWithNewCamera = async () => {
    if (!codeReaderRef.current || !availableCameras.length || !videoRef.current) return;

    try {
      // Select new camera based on facing mode
      let selectedDeviceId;
      if (cameraFacing === 'user') { // Switching TO front camera
        const frontCamera = availableCameras.find(device => 
          device.label.toLowerCase().includes('front') || 
          device.label.toLowerCase().includes('user') ||
          device.label.toLowerCase().includes('face')
        );
        selectedDeviceId = frontCamera ? frontCamera.deviceId : availableCameras[availableCameras.length - 1].deviceId;
      } else { // Switching TO back camera
        const environmentCamera = availableCameras.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('environment') ||
          device.label.toLowerCase().includes('rear')
        );
        selectedDeviceId = environmentCamera ? environmentCamera.deviceId : availableCameras[0].deviceId;
      }

      // Stop the old stream gracefully
      const oldStream = videoRef.current.srcObject;
      if (oldStream) {
        oldStream.getTracks().forEach(track => track.stop());
      }

      // Get new stream
      const constraints = {
        deviceId: { exact: selectedDeviceId },
        width: { min: 640, ideal: 1280, max: 1920 },
        height: { min: 480, ideal: 720, max: 1080 },
        facingMode: cameraFacing,
      };

      const newStream = await navigator.mediaDevices.getUserMedia({ video: constraints });
      
      // Switch video source - video element has fixed dimensions so no resizing
      videoRef.current.srcObject = newStream;

    } catch (error) {
      console.error('Error switching camera:', error);
      // Fallback to restart full scanner if direct stream switch fails
      setTimeout(() => startScanner(), 100);
    }
  };

  const sendBarcodeData = useCallback((barcode) => {
    // Use environment variable if set, otherwise determine based on environment
    const isDevelopment = process.env.NODE_ENV === 'development';
    let baseUrl;
    
    if (process.env.REACT_APP_API_BASE_URL) {
      // Use explicitly configured API base URL
      baseUrl = process.env.REACT_APP_API_BASE_URL;
    } else if (isDevelopment) {
      // Development fallback
      baseUrl = 'http://localhost:8080';
    } else {
      // Production fallback - use same origin
      baseUrl = window.location.origin;
    }
    
    const fullUrl = `${baseUrl}/api/product?barcode=${barcode}`;
    
    // Debug logging
    // console.log('🔧 API Configuration Debug:', {
    //   NODE_ENV: process.env.NODE_ENV,
    //   REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
    //   isDevelopment,
    //   baseUrl,
    //   fullUrl,
    //   windowOrigin: window.location.origin
    // });
    
    fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
    .then(async response => {
      if (!response.ok) {
        if (response.status === 404) {
          // Product not found - show add button
          setShowAddProduct(true);
          setNewProduct(prev => ({ ...prev, barcode }));
          setUserFriendlyError(
            "Produkt nebyl nalezen. Můžete ho přidat do databáze.",
            `Product not found for barcode: ${barcode}`
          );
          cache.current.set(barcode, null);
          return;
        }
        const text = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${text}`);
      }
      return response.json();
    })
    .then(data => {
      if (data) {
        // Ensure we're using the product data correctly
        const productData = data.product || data;
        setProductData(productData);
        setImageLoadError(false); // Reset image error state
        setErrorMessage('');
        setDebugErrorMessage('');
        cache.current.set(barcode, productData);
        stopScanner();
        setShowAddProduct(false);
      } else {
        setShowAddProduct(true);
        setNewProduct(prev => ({ ...prev, barcode }));
        setUserFriendlyError(
          "Produkt nebyl nalezen. Můžete ho přidat do databáze.",
          `API returned success: false for barcode: ${barcode}`
        );
        cache.current.set(barcode, null);
      }
    })
    .catch(error => {
      // User-friendly error messages based on common issues
      let userMessage = "Nepodarilo se načíst informace o produktu. ";
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        userMessage = "Problém s připojením k internetu. Zkontrolujte prosím připojení a zkuste to znovu.";
      } else if (error.message.includes('404')) {
        userMessage = "Služba není momentálně dostupná. Zkuste to prosím později.";
      } else if (error.message.includes('500')) {
        userMessage = "Problém se serverem. Zkuste to prosím za chvíli.";
      } else {
        userMessage = "Neočekávaná chyba. Zkuste to prosím znovu nebo použijte ruční zadání.";
      }
      
      setUserFriendlyError(userMessage, `API Error: ${error.message}`);
    });
  }, []);

  const handleDetected = useCallback((code) => {
    if (!code || code.length < 8) {
      return;
    }

    if (cache.current.has(code)) {
      const cachedData = cache.current.get(code);
      if (cachedData) {
        setProductData(cachedData);
        stopScanner();
        return;
      }
    }

    sendBarcodeData(code);
  }, [sendBarcodeData]);

  const startScanner = useCallback(async () => {
    try {
      // Check if getUserMedia is supported - Safari compatible check
      const mediaDevices = navigator.mediaDevices || 
                          (navigator.mediaDevices = {});
      
      if (!mediaDevices.getUserMedia) {
        mediaDevices.getUserMedia = function(constraints) {
          const getUserMedia = navigator.webkitGetUserMedia || navigator.mozGetUserMedia;
          
          if (!getUserMedia) {
            return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
          }
          
          return new Promise(function(resolve, reject) {
            getUserMedia.call(navigator, constraints, resolve, reject);
          });
        }
      }

      // Check if we're on HTTPS (required for camera access)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        const error = "Camera access requires HTTPS. Current protocol: " + window.location.protocol;
        setUserFriendlyError(
          "Kamera vyžaduje zabezpečené připojení (HTTPS). Zkuste akci opakovat nebo použijte ruční zadání.",
          error
        );
        return;
      }

      codeReaderRef.current = new BrowserMultiFormatReader();
      
      // List available video devices - Safari compatible
      let videoInputDevices;
      try {
        // For Safari, we might need to request permission first
        if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
          try {
            const testStream = await navigator.mediaDevices.getUserMedia({ video: true });
            testStream.getTracks().forEach(track => track.stop());
          } catch (safariError) {
            setUserFriendlyError(
              "Nepodarilo se získat přístup ke kameře. Povolte prosím přístup ke kameře v nastavení Safari.",
              `Safari Camera Error: ${safariError.message}`
            );
            return;
          }
        }
        
        videoInputDevices = await codeReaderRef.current.listVideoInputDevices();
        
        if (!videoInputDevices || videoInputDevices.length === 0) {
          const error = "No camera devices found";
          setUserFriendlyError(
            "Kamera nebyla nalezena. Zkontrolujte prosím, zda má vaše zařízení kameru a povolte přístup.",
            error
          );
          return;
        }

        // Store available cameras for UI
        setAvailableCameras(videoInputDevices);
      } catch (deviceError) {
        setUserFriendlyError(
          "Chyba při přístupu ke kameře. Zkontrolujte prosím oprávnění kamery v nastavení prohlížeče.",
          `Device Error: ${deviceError.message}`
        );
        return;
      }

      // Select camera based on the current facing mode
      let selectedDeviceId;
      if (cameraFacing === 'environment') {
        // Try to use the back/environment camera
        const environmentCamera = videoInputDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('environment') ||
          device.label.toLowerCase().includes('rear')
        );
        selectedDeviceId = environmentCamera ? environmentCamera.deviceId : videoInputDevices[0].deviceId;
      } else {
        // Try to use the front/user camera
        const frontCamera = videoInputDevices.find(device => 
          device.label.toLowerCase().includes('front') || 
          device.label.toLowerCase().includes('user') ||
          device.label.toLowerCase().includes('face')
        );
        selectedDeviceId = frontCamera ? frontCamera.deviceId : videoInputDevices[0].deviceId;
      }

      // Test camera access first
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({ 
          video: { deviceId: selectedDeviceId } 
        });
        testStream.getTracks().forEach(track => track.stop()); // Clean up test stream
      } catch (cameraError) {
        let userMessage = "Nepodarilo se spustit kameru. ";
        let debugMessage = `Camera error: ${cameraError.message}`;
        
        if (cameraError.name === 'NotAllowedError') {
          userMessage = "Přístup ke kameře byl zamítnut. Povolte prosím přístup ke kameře v nastavení prohlížeče.";
        } else if (cameraError.name === 'NotFoundError') {
          userMessage = "Kamera nebyla nalezena. Zkontrolujte prosím, zda má vaše zařízení funkční kameru.";
        } else if (cameraError.name === 'NotReadableError') {
          userMessage = "Kamera je používána jinou aplikací. Zavřete prosím ostatní aplikace používající kameru.";
        } else if (cameraError.name === 'OverconstrainedError') {
          userMessage = "Kamera nepodporuje požadované nastavení. Zkuste jiný prohlížeč nebo kameru.";
        }
        
        setUserFriendlyError(userMessage, debugMessage);
        return;
      }

      // Add constraints for better video quality - Safari compatible
      const constraints = {
        deviceId: selectedDeviceId,
        width: { min: 640, ideal: 1280, max: 1920 },
        height: { min: 480, ideal: 720, max: 1080 },
        facingMode: cameraFacing, // Use the selected camera facing
      };

      // Note: Zoom is now handled separately via applyZoomToCurrentTrack()
      await codeReaderRef.current.decodeFromVideoDevice(
        selectedDeviceId,
        videoRef.current,
        (result, error) => {
          if (result) {
            handleDetected(result.getText());
          }
          // Only log significant errors
          if (error && 
              !(error instanceof TypeError) && 
              error.message !== 'No MultiFormat Readers were able to detect the code.') {
            // console.error("Scanning error:", error);
          }
        },
        constraints
      );

      // Add a click handler to the video element to trigger auto-focus
      if (videoRef.current) {
      videoRef.current.addEventListener('click', () => {
          if (videoRef.current && videoRef.current.srcObject) {
          const track = videoRef.current.srcObject.getVideoTracks()[0];
          if (track.getCapabilities().focusMode) {
            track.applyConstraints({
              advanced: [{ focusMode: "continuous" }]
            }).catch(e => {
              // console.log('Focus error:', e);
            });
          }
        }
      });
      }

      isScanning.current = true;

      // Camera will use its completely natural default settings
      // Zoom detection will only happen when user interacts with zoom button

    } catch (err) {
      let userMessage = "Nepodarilo se spustit skener. Zkuste prosím ruční zadání.";
      let debugMessage = `Scanner startup error: ${err.message}`;
      
      if (err.name === 'NotAllowedError') {
        userMessage = "Přístup ke kameře byl zamítnut. Povolte prosím přístup ke kameře a akci opakujte.";
      } else if (err.name === 'NotFoundError') {
        userMessage = "Kamera nebyla nalezena. Zkontrolujte prosím, zda má vaše zařízení kameru.";
      } else if (err.name === 'NotReadableError') {
        userMessage = "Kamera je používána jinou aplikací. Zavřete prosím ostatní aplikace používající kameru.";
      } else if (err.name === 'OverconstrainedError') {
        userMessage = "Kamera nepodporuje požadované nastavení. Zkuste jiný prohlížeč.";
      }
      
      setUserFriendlyError(userMessage, debugMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDetected, cameraFacing]);

  useEffect(() => {
    // Only apply zoom if user has explicitly changed it and camera is scanning
    if (isScanning.current && zoomLevel !== null) {
      applyZoomToCurrentTrack();
    }
  }, [zoomLevel, applyZoomToCurrentTrack]);

  const stopScanner = () => {
    if (codeReaderRef.current && isScanning.current) {
      codeReaderRef.current.reset();
      isScanning.current = false;
    }
  };

  const handleManualSubmit = () => {
    if (manualCode.length > 0) {
      sendBarcodeData(manualCode);
      setManualCode('');
    }
  };

  const handleScanAgain = () => {
    setProductData(null);
    setErrorMessage('');
    setDebugErrorMessage('');
    startScanner();
  };

      const handleAddProduct = async () => {
    if (!newProduct.barcode || !newProduct.product_name) return;

    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    
    try {
      const response = await fetch(`${baseUrl}/api/product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newProduct)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Success - close dialog and show success message
      setUserFriendlyError('Produkt byl úspěšně přidán!', '');
      setAddProductDialogOpen(false);
      setShowAddProduct(false);
      
      // Fetch the newly created product
      sendBarcodeData(newProduct.barcode);
    } catch (error) {
      setUserFriendlyError(
        'Nepodařilo se přidat produkt. Zkuste to prosím později.',
        `Add product error: ${error.message}`
      );
    }
  };

  const handleReportSubmit = async () => {
      if (!productData?.code || !reportDescription || !reportType) return;

    setReportSubmitting(true);
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    
    try {
      const response = await fetch(`${baseUrl}/api/product/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          barcode: productData.code,
          description: reportDescription,
          report_type: reportType
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Success
      setUserFriendlyError('Děkujeme za nahlášení produktu!', '');
      setReportDialogOpen(false);
      setReportType('');
      setReportDescription('');
    } catch (error) {
      setUserFriendlyError(
        'Nepodařilo se odeslat hlášení. Zkuste to prosím později.',
        `Report submission error: ${error.message}`
      );
    } finally {
      setReportSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      startScanner();
    }
    return () => {
      stopScanner();
    };
  }, [open, startScanner]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm" 
      fullWidth
      aria-labelledby="camera-dialog-title"
      aria-describedby="camera-dialog-description"
      container={() => document.body}
      disablePortal={false}
      disableEnforceFocus={true}
      disableAutoFocus={false}
      disableRestoreFocus={true}
      scroll="body"
      sx={{
        zIndex: 1200,
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
        '& .MuiDialog-container': {
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'center',
          paddingTop: { xs: '8px', sm: '0' },
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: 'transparent',
          border: 'none',
          boxShadow: 'none',
          margin: { xs: '8px 2px', sm: 1 },
          maxHeight: { xs: 'calc(100vh - 32px)', sm: 'calc(100vh - 32px)' },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transform: 'none !important',
          top: 'auto !important',
          left: 'auto !important',
        }
      }}
    >
      {!productData ? (
        <Paper 
          elevation={8}
          sx={{ 
            borderRadius: 4,
            background: '#ffffff',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            maxHeight: '100%',
          }}
        >
          {/* Header */}
          <Box 
            sx={{ 
              p: 2, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
              color: 'white'
            }}
          >
            <Typography 
              id="camera-dialog-title"
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}
            >
              Naskenujte čárový kód
            </Typography>
          </Box>

          {/* Camera Section */}
          <Box 
            id="camera-dialog-description"
            sx={{ 
              position: 'relative',
              backgroundColor: '#000',
              borderRadius: 3,
              mx: 2,
              mt: 2,
              overflow: 'hidden',
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <video 
              ref={videoRef} 
              style={{ 
                width: '100%', 
                height: '100%',
                objectFit: 'cover',
                backgroundColor: '#000',
                borderRadius: '12px'
              }} 
              playsInline
              autoPlay
              muted
              aria-label="Kamera pro skenování čárových kódů"
            />
            
            {/* Camera Controls */}
            {availableCameras.length > 1 && (
              <Button
                onClick={toggleCamera}
                variant="contained"
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  minWidth: 'auto',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#333',
                  fontSize: '1.2rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease'
                }}
                title={cameraFacing === 'environment' ? 'Přepnout na přední kameru' : 'Přepnout na zadní kameru'}
              >
                {cameraFacing === 'environment' ? '🤳' : '📷'}
              </Button>
            )}
            
            <Button
              onClick={toggleZoom}
              variant="contained"
              size="small"
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                minWidth: 'auto',
                px: 2,
                height: 48,
                borderRadius: 24,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#333',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease'
              }}
              title={
                availableZoomLevels.length > 0 
                  ? `Přepnout na ${availableZoomLevels[(currentZoomIndex + 1) % availableZoomLevels.length]?.label || 'další'} zoom`
                  : 'Aktivovat zoom'
              }
            >
              🔍
            </Button>

            {/* Add Product Button */}
            {(showAddProduct || errorMessage.includes("Produkt nebyl nalezen")) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => setAddProductDialogOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: '#4caf50',
                    borderRadius: '50%',
                    width: 64,
                    height: 64,
                    minWidth: 64,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    '&:hover': {
                      backgroundColor: 'white',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Typography variant="h4" component="span">+</Typography>
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 'medium'
                  }}
                >
                  Přidat produkt
                </Typography>
              </Box>
            )}

            {/* Add Product Dialog */}
            <Dialog
              open={addProductDialogOpen}
              onClose={() => setAddProductDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              sx={{
                zIndex: 1400,
                '& .MuiDialog-container': {
                  alignItems: 'center',
                  justifyContent: 'center'
                }
              }}
            >
              <Box 
                sx={{ 
                  p: 3,
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: '#fff'
                }}
              >
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Přidat nový produkt
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Obsahuje lepek?
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: 1, 
                  mb: 3,
                  '& .MuiButtonBase-root': {
                    borderRadius: 1.5,
                    py: 1,
                    px: 1.5,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    border: '1px solid transparent',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                    '&.selected': {
                      borderColor: (theme) => theme.palette.primary.main,
                      backgroundColor: 'rgba(76, 175, 80, 0.08)',
                    }
                  }
                }}>
                  <Button
                    onClick={() => setNewProduct(prev => ({ ...prev, gluten: 'gluten-free' }))}
                    className={newProduct.gluten === 'gluten-free' ? 'selected' : ''}
                  >
                    <Box sx={{ fontSize: '1.5rem', lineHeight: 1, color: 'success.main' }}>✅</Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        whiteSpace: 'nowrap',
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        color: 'success.main',
                        fontWeight: 'medium'
                      }}
                    >
                      BEZ LEPKU
                    </Typography>
                  </Button>

                  <Button
                    onClick={() => setNewProduct(prev => ({ ...prev, gluten: 'traces-of-gluten' }))}
                    className={newProduct.gluten === 'traces-of-gluten' ? 'selected' : ''}
                  >
                    <Box sx={{ fontSize: '1.5rem', lineHeight: 1 }}>⚠️</Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        whiteSpace: 'nowrap',
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        color: 'warning.main',
                        fontWeight: 'medium'
                      }}
                    >
                      STOPY LEPKU
                    </Typography>
                  </Button>

                  <Button
                    onClick={() => setNewProduct(prev => ({ ...prev, gluten: 'contains-gluten' }))}
                    className={newProduct.gluten === 'contains-gluten' ? 'selected' : ''}
                  >
                    <Box sx={{ fontSize: '1.5rem', lineHeight: 1 }}>🚫</Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        whiteSpace: 'nowrap',
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        color: 'error.main',
                        fontWeight: 'medium'
                      }}
                    >
                      OBSAHUJE
                    </Typography>
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  label="Čárový kód"
                  value={newProduct.barcode}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, barcode: e.target.value }))}
                  disabled
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Název produktu"
                  value={newProduct.product_name}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, product_name: e.target.value }))}
                  sx={{ mb: 3 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    onClick={() => setAddProductDialogOpen(false)}
                    variant="outlined"
                    sx={{
                      borderRadius: 50,
                      px: 3,
                      borderColor: '#ff6b6b',
                      color: '#ff6b6b',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        borderColor: '#ff6b6b',
                      }
                    }}
                  >
                    Zrušit
                  </Button>
                  <Button
                    onClick={handleAddProduct}
                    variant="contained"
                    disabled={!newProduct.product_name}
                    sx={{
                      borderRadius: 50,
                      px: 3,
                      background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #45a049 0%, #388e3c 100%)',
                      }
                    }}
                  >
                    Přidat produkt
                  </Button>
                </Box>
              </Box>
            </Dialog>
          </Box>

          {/* Manual Input Section */}
          <Box sx={{ px: 2, py: 2 }}>
            <TextField
              label="Nebo zadejte kód ručně"
              variant="outlined"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  '&.Mui-focused fieldset': {
                    borderColor: '#ff6b6b',
                  }
                },
                '& .MuiInputLabel-root': {
                  '&.Mui-focused': {
                    color: '#ff6b6b',
                  }
                }
              }}
            />
          </Box>

          {/* Button Bar */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              px: 2,
              pb: 3,
              gap: 2
            }}
          >
            <Button 
              onClick={onClose}
              variant="outlined"
              sx={{ 
                borderRadius: 50,
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                textTransform: 'none',
                borderColor: '#ff6b6b',
                color: '#ff6b6b',
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  borderColor: '#ff6b6b',
                }
              }}
            >
              Zavřít
            </Button>
            
            <Button 
              onClick={handleManualSubmit}
              variant="contained"
              disabled={!manualCode}
              sx={{ 
                borderRadius: 50,
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff5252 0%, #e53e3e 100%)',
                  boxShadow: '0 6px 20px rgba(255, 107, 107, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: '#ccc',
                  boxShadow: 'none',
                }
              }}
            >
              Odeslat
            </Button>
          </Box>

          {/* Error Message */}
          {errorMessage && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography 
                color="error" 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(255, 0, 0, 0.1)',
                  borderRadius: 2,
                  fontSize: '0.9rem'
                }}
              >
                {errorMessage}
              </Typography>
              
              {/* Debug Mode - Show detailed error */}
              {isDebugMode && debugErrorMessage && (
                <Box sx={{ mt: 1 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      p: 1, 
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      display: 'block',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    DEBUG: {debugErrorMessage}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      ) : (
        /* Product Found Card */
        <Paper 
          elevation={8}
          sx={{ 
            borderRadius: 4,
            background: '#ffffff',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            maxHeight: '100%',
          }}
        >
          <Card sx={{ 
            border: 'none', 
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'auto',
          }}>
            {/* Report Button */}
            <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 1 }}>
              <IconButton
                onClick={() => setReportDialogOpen(true)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                }}
                title="Nahlásit produkt"
              >
                <ReportIcon color="error" />
              </IconButton>
            </Box>

            {/* Report Dialog */}
            <Dialog
              open={reportDialogOpen}
              onClose={() => setReportDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              container={() => document.body}
              disableEnforceFocus={true}
              disableRestoreFocus={true}
              sx={{
                zIndex: 1400,
                '& .MuiDialog-container': {
                  alignItems: 'center',
                  justifyContent: 'center'
                }
              }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  p: 2,
                  backgroundColor: '#fff',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  width: '100%',
                  maxWidth: '500px',
                  position: 'relative',
                  zIndex: 1401
                }
              }}
              BackdropProps={{
                sx: { 
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 1400
                }
              }}
              slotProps={{
                backdrop: {
                  sx: { zIndex: 1400 }
                }
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Nahlásit produkt
              </Typography>

              <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Vyberte typ hlášení:
                </Typography>
                <ToggleButtonGroup
                  value={reportType}
                  exclusive
                  onChange={(e, newValue) => setReportType(newValue)}
                  aria-label="typ hlášení"
                  sx={{
                    mb: 2,
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: '12px !important',
                      mx: 0.5,
                      p: 2,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        color: '#ff6b6b',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 107, 107, 0.2)',
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton 
                    value="incorrect_gluten_info" 
                    aria-label="nesprávná informace o lepku"
                    sx={{ 
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <GrainIcon />
                    <Typography variant="caption">Lepek</Typography>
                  </ToggleButton>
                  <ToggleButton 
                    value="incorrect_product_info" 
                    aria-label="nesprávné informace o produktu"
                    sx={{ 
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <TextFieldsIcon />
                    <Typography variant="caption">Text</Typography>
                  </ToggleButton>
                  <ToggleButton 
                    value="incorrect_image" 
                    aria-label="nesprávný obrázek"
                    sx={{ 
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <ImageIcon />
                    <Typography variant="caption">Obrázek</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Popis problému"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  onClick={() => setReportDialogOpen(false)}
                  variant="outlined"
                  sx={{
                    borderRadius: 50,
                    px: 3,
                    borderColor: '#ff6b6b',
                    color: '#ff6b6b',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 107, 107, 0.1)',
                      borderColor: '#ff6b6b',
                    }
                  }}
                >
                  Zrušit
                </Button>
                <Button
                  onClick={handleReportSubmit}
                  variant="contained"
                  disabled={!reportDescription || !reportType || reportSubmitting}
                  sx={{
                    borderRadius: 50,
                    px: 3,
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ff5252 0%, #e53e3e 100%)',
                    }
                  }}
                >
                  {reportSubmitting ? 'Odesílám...' : 'Odeslat'}
                </Button>
              </Box>
            </Dialog>

            {/* Product Image or Fallback */}
            <Box sx={{ pt: 3, pb: 2, px: 2 }}>
              {productData.image_url && !imageLoadError ? (
                <Box
                  component="img"
                  src={productData.image_url}
                  alt={productData.product_name || 'Product Image'}
                  onLoad={() => {}}
                  onError={(e) => {
                    setImageLoadError(true);
                  }}
                  sx={{ 
                    height: 250, 
                    width: 'auto', 
                    maxWidth: '100%',
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    objectFit: 'contain',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 250,
                    width: '100%',
                    maxWidth: 300,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    border: '2px dashed #ddd',
                    color: '#999'
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '3rem',
                      mb: 1,
                      opacity: 0.5
                    }}
                  >
                    📄
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      textAlign: 'center',
                      px: 2
                    }}
                  >
                    {imageLoadError ? 'Obrázek se nepodařilo načíst' : 'Obrázek není dostupný'}
                  </Typography>
                  {imageLoadError && productData?.image_url && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mt: 1,
                        fontSize: '0.65rem',
                        color: '#666',
                        wordBreak: 'break-all',
                        px: 2,
                        textAlign: 'center'
                      }}
                    >
                      {productData.image_url}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            
            <CardContent sx={{ 
              textAlign: 'center', 
              pt: 0,
              flex: 1,
              overflow: 'auto',
            }}>
              <Typography gutterBottom variant="h4" component="div">
                {productData.product_name || 'Neznámý produkt'}
              </Typography>

              <Box display="flex" flexDirection="column" alignItems="center" gap={1} my={2}>
                <Box>
                  {glutenFreeChecker(productData) === 'gluten-free' ? (
                    <Chip label="Bez lepku 🥳" color="success" />
                  ) : glutenFreeChecker(productData) === 'contains-gluten' ? (
                    <Chip label="Obsahuje lepek 🤢" color="error" />
                  ) : glutenFreeChecker(productData) === 'traces-of-gluten' ? (
                    <Chip label="Obsahuje stopky lepku 🤨" color="warning" />
                  ) : glutenFreeChecker(productData) === 'unknown' ? (
                    <Chip label="Nevíme 🤔 " color="info" />
                  ) : (
                    <Chip label="Nejsme si jistí 🤔 " color="info" />
                  )}
                </Box>
                <Box>
                  {(productData.product_status === 'pending' || productData.status === 0) ? (
                    <Chip 
                      label="Provádíme review" 
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderStyle: 'dashed',
                        fontSize: '0.7rem',
                        height: '20px',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  ) : productData.product_status === 'approved' || productData.status === 1 ? (
                    <Chip 
                      label="Zkontrolováno" 
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.7rem',
                        height: '20px',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  ) : null}
                </Box>
              </Box>

              <Typography variant="body1" color="text.secondary">
                Značka: {productData.brands || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Kód: {productData.code || 'N/A'}
              </Typography>
            </CardContent>
          </Card>

          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              p: 2,
              pb: 3,
              gap: 2
            }}
          >
            <Button 
              onClick={onClose}
              variant="outlined"
              sx={{ 
                borderRadius: 50,
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                textTransform: 'none',
                borderColor: '#ff6b6b',
                color: '#ff6b6b',
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  borderColor: '#ff6b6b',
                }
              }}
            >
              Zavřít
            </Button>
            
            <Button 
              onClick={handleScanAgain}
              variant="contained"
              sx={{ 
                borderRadius: 50,
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff5252 0%, #e53e3e 100%)',
                  boxShadow: '0 6px 20px rgba(255, 107, 107, 0.4)',
                  transform: 'translateY(-1px)',
                }
              }}
            >
              Skenovat znovu
            </Button>
          </Box>
        </Paper>
      )}
    </Dialog>
  );
};

const glutenFreeChecker = (product) => {
  if (!product) return 'unknown';
  return product.gluten || 'unknown';
};

export default CameraDialog;